import * as React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { GradientText, SectionLabel } from "./GradientText";
import { Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

interface BenefitItem {
  title: string;
  description: string;
}

/**
 * Benefits
 *
 * Two-column layout with:
 * - Left: Sticky headline, subtitle, and CTA
 * - Right: Stacked benefit cards with checkmark icons
 * - All text from translations
 */
export function Benefits() {
  const { t } = useTranslation("landing");
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  const title = t("benefits.title");
  const subtitle = t("benefits.subtitle");
  const items = t("benefits.items", { returnObjects: true }) as BenefitItem[];

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Left column - Sticky header */}
          <div
            className={cn(
              "lg:sticky lg:top-32",
              "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
              isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
            )}
          >
            <SectionLabel className="mb-6">Benefits</SectionLabel>

            <h2 className="mb-4 text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
              <GradientText>{title}</GradientText>
            </h2>

            {subtitle && (
              <p className="mb-8 text-base text-muted-foreground lg:text-lg">{subtitle}</p>
            )}

            <Button className={cn("transition-all duration-300")}>
              See all benefits
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>

          {/* Right column - Benefit cards */}
          <div className="space-y-4">
            {Array.isArray(items) &&
              items.map((item, index) => (
                <div
                  key={index}
                  className={cn(
                    "group relative",
                    "transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]",
                    isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
                  )}
                  style={{
                    transitionDelay: isVisible ? `${(index + 1) * 100}ms` : "0ms",
                  }}
                >
                  {/* Card */}
                  <div
                    className={cn(
                      "relative overflow-hidden rounded-2xl",
                      "border border-border bg-card",
                      "p-6",
                      "transition-all duration-300 ease-out",
                      "hover:-translate-y-0.5 hover:border-border",
                    )}
                  >
                    <div className="flex gap-4">
                      {/* Checkmark icon */}
                      <div
                        className={cn(
                          "relative h-12 w-12 shrink-0 rounded-xl",
                          "bg-muted",
                          "border border-primary/30",
                          "flex items-center justify-center",
                          "transition-all duration-300",
                          "group-hover:border-primary/50",
                        )}
                      >
                        <Check className="h-5 w-5 text-primary transition-colors duration-300 group-hover:text-primary" />
                      </div>

                      {/* Content */}
                      <div className="min-w-0 flex-1">
                        <h3 className="mb-1 text-lg font-semibold text-foreground transition-colors duration-300">
                          {item.title}
                        </h3>
                        <p className="text-sm leading-relaxed text-muted-foreground">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </section>
  );
}
